import { EGerdauService } from '../EGerdauService';
import {
  IBilledByDayData,
  IBilledByDayProduct,
  IBilledMonthData,
  IDuplicateByDay,
  IDuplicateData,
  IDuplicatesByDayData,
  IOpenOrderStatusData,
  IOpenOrderStatusDataBySku,
  IPaidByMonthData
} from '../../store/ducks/dashboard/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { ELocalStorageKey } from '../EGerdauService/types';
import moment from 'moment';
import { ECustomerTypeView } from '../../store/ducks/user/types';

export class DashboardService extends EGerdauService {
  readonly localStorageKey = ELocalStorageKey.DASHBOARD;

  async loadOpenOrderStatus(
    customerId: string,
    customerViewType: ECustomerTypeView
  ): Promise<IOpenOrderStatusData[]> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const url = `${ECustomerAuthorizationEndpoints.OPEN_ORDER_STATUS}?customer=${customerId}&customerType=${customerType}`;
    const openOrderStatus = await customersAuthorizationApi.get(url, {
      headers
    });
    return openOrderStatus.data.dashboard as IOpenOrderStatusData[];
  }

  async loadOpenOrderStatusBySku(
    customerId: string,
    customerViewType: ECustomerTypeView
  ): Promise<IOpenOrderStatusDataBySku[]> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const url = `${ECustomerAuthorizationEndpoints.OPEN_ORDER_STATUS_BY_SKU}?customer=${customerId}&customerType=${customerType}&skuDetail=X`;
    const openOrderStatus = await customersAuthorizationApi.get(url, {
      headers
    });
    return openOrderStatus.data.dashboard as IOpenOrderStatusDataBySku[];
  }

  async loadBilledByMonth(
    customerId: string,
    customerViewType: ECustomerTypeView
  ): Promise<IBilledMonthData[]> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.BILLED_BY_MONTH}?customer=${customerId}&customerType=${customerType}`,
      { headers }
    );
    const billedMonthData = response.data as IBilledMonthData[];

    billedMonthData
      .sort((a, b) =>
        moment(a.date, 'YYYYMMDD').isAfter(moment(b.date, 'YYYYMMDD')) // ordena por ordem crescente
          ? 1
          : moment(a.date, 'YYYYMMDD').isBefore(moment(b.date, 'YYYYMMDD'))
          ? -1
          : 0
      )
      .forEach((item) => {
        // coloca no formato "jan/2021" para usar no gráfico
        item.date = moment(item.date, 'YYYYMMDD').format('MMM/YYYY');
        item.total = Number(item.total);
      });

    return billedMonthData;
  }

  async loadPaidByMonth(
    customerId: string,
    customerViewType: ECustomerTypeView
  ): Promise<IPaidByMonthData[]> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.PAID_BY_MONTH}?customer=${customerId}&customerType=${customerType}`,
      { headers }
    );
    const paidMonthData = response.data as IPaidByMonthData[];

    paidMonthData
      .sort((a, b) =>
        moment(a.invoicesSummary.date, 'YYYYMMDD').isAfter(
          moment(b.invoicesSummary.date, 'YYYYMMDD')
        ) // ordena por ordem crescente
          ? 1
          : moment(a.invoicesSummary.date, 'YYYYMMDD').isBefore(
              moment(b.invoicesSummary.date, 'YYYYMMDD')
            )
          ? -1
          : 0
      )
      .forEach((item) => {
        // coloca no formato "jan/2021" para usar no gráfico
        item.invoicesSummary.date = moment(
          item.invoicesSummary.date,
          'YYYYMMDD'
        ).format('MMM/YYYY');
        item.invoicesSummary.totalInvoice =
          item.invoicesSummary.totalInvoice?.replace(/,/g, '') || '0';
      });

    return paidMonthData;
  }

  async loadDuplicates(
    customerId: string,
    customerViewType: ECustomerTypeView
  ): Promise<IDuplicateData> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const today = moment().format('YYYYMMDD');
    const duplicates = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.DUPLICATES}?customer=${customerId}&invoiceDate=${today}&customerType=${customerType}`,
      { headers }
    );
    return duplicates.data.invoicesSummary as IDuplicateData;
  }

  async loadBilledByDay(
    customerId: string,
    billedByDayMonthDate: moment.Moment,
    customerViewType: ECustomerTypeView
  ): Promise<IBilledByDayData[]> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const startDate = billedByDayMonthDate.startOf('month').format('YYYYMMDD');
    const endDate = billedByDayMonthDate.endOf('month').format('YYYYMMDD');
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.BILLED_BY_DAY}?customer=${customerId}&startDate=${startDate}&endDate=${endDate}&&customerType=${customerType}`,
      { headers }
    );

    const billedByDayResponse = response.data as {
      billedToday: number;
      date: string;
      division: string;
      divisionDescription: string;
    }[];
    const billedByDay: IBilledByDayData[] = [];

    const allDaysInMonth = this.getAllDaysInMonth(billedByDayMonthDate);

    allDaysInMonth.forEach((day) => {
      const dateProducts: IBilledByDayProduct[] = billedByDayResponse
        .filter((item) => moment(item.date, 'YYYYMMDD').format('DD/MM') === day)
        .map((item) => {
          return {
            division: item.division,
            divisionDescription: item.divisionDescription,
            billedToday: item.billedToday
          };
        });

      billedByDay.push({
        date: day,
        products: dateProducts
      });
    });

    return billedByDay;
  }

  async loadDuplicatesByDay(
    customerId: string,
    duplicatesByDayMonthDate: moment.Moment,
    customerViewType: ECustomerTypeView
  ): Promise<IDuplicatesByDayData[]> {
    const headers = this.getHeaders();
    const customerType =
      customerViewType === ECustomerTypeView.EMITTER
        ? customerViewType
        : ECustomerTypeView.RECEIVER;
    const startDate = duplicatesByDayMonthDate
      .startOf('month')
      .format('YYYYMMDD');
    const endDate = duplicatesByDayMonthDate.endOf('month').format('YYYYMMDD');
    const response = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.DUPLICATES_BY_DAY}?customer=${customerId}&startDueDate=${startDate}&endDueDate=${endDate}&customerType=${customerType}`,
      { headers }
    );
    const duplicateResponse = response.data as {
      totalInvoice: number;
      date: string;
      status: string;
    }[];

    const duplicatesByDay: IDuplicatesByDayData[] = [];
    const allDaysInMonth = this.getAllDaysInMonth(duplicatesByDayMonthDate);
    allDaysInMonth.forEach((day) => {
      const dateProducts: IDuplicateByDay[] = duplicateResponse
        .filter((item) => moment(item.date, 'YYYYMMDD').format('DD/MM') === day)
        .map((item) => {
          return {
            status: item.status,
            totalInvoice: Number(item.totalInvoice.toString().replace(/,/g, ''))
          };
        });

      duplicatesByDay.push({
        date: day,
        duplicates: dateProducts
      });
    });

    return duplicatesByDay;
  }

  private getAllDaysInMonth(monthDate: moment.Moment): string[] {
    const firstDayInMonth = moment(
      monthDate.format('DDMMYYYY'),
      'DDMMYYYY'
    ).startOf('month');
    const lastDayInMonth = moment(
      monthDate.format('DDMMYYYY'),
      'DDMMYYYY'
    ).endOf('month');

    const allDaysInMonth = [firstDayInMonth.format('DD/MM')];

    for (
      let i = firstDayInMonth.get('date');
      i < lastDayInMonth.get('date');
      i++
    ) {
      allDaysInMonth.push(firstDayInMonth.add(1, 'day').format('DD/MM'));
    }
    return allDaysInMonth;
  }
}
